import React, { useState } from 'react';
import { manuallyPatientUpdate } from '../../../services/user.service';
import Dialog from '../../ui/Dialog';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay.component';
import { Button, makeStyles, TextField, Typography, Grid, Select, MenuItem, InputLabel, FormControl ,CircularProgress} from '@material-ui/core';
import DatePicker from '../../DatePicker/DatePicker.component';

interface FormErrors {
  first_name?: string;
  last_name?: string;
  mobile_number?: string;
  birth_date?: string;
  preferred_language?: string;
  prescribed_medicine?: string;
  support_type?: string;
}

interface PatientDetails {
  first_name: string;
  last_name: string;
  birth_date: string | null;
  mobile_number: string | null;
  preferred_language: string;
  prescribed_medicine: string;
  support_type: string;
}

interface ApiError {
  inputs?: Record<string, string>;
  message?: string;
}

const UpdateCustomerDetails = ({ data }) => {
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmation, setConfirmation] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [errorInputMessage, setErrorInputMessage] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  
  
  const useStyles = makeStyles(() => ({
    field: {
      marginBottom: 10,
      width: '100%'
    },
    requiredLabel: {
      '& .MuiInputLabel-asterisk': {
        color: 'red'
      }
    }
  }));
  
  const classes = useStyles();
  const { 
    first_name, 
    last_name,
    birth_date, 
    mobile_number,
    preferred_language,
    prescribed_medicine,
    patient_id, 
    program_id,
    email,
    support_type
  } = data;

  const currentDate = new Date();

  const getSupportTypeOptions = (medicine: string) => {
    if (medicine === "benlysta") {
      return [<MenuItem key="AFN" value="AFN">AFN</MenuItem>];
    }
    return [
      <MenuItem key="AFN" value="AFN">AFN</MenuItem>,
      <MenuItem key="NE" value="NE">NE</MenuItem>,
      <MenuItem key="AFN,NE" value="AFN,NE">AFN,NE</MenuItem>
    ];
  };

  const [patientDetails, setPatientDetails] = useState<PatientDetails>({
    first_name,
    last_name,
    birth_date,
    mobile_number: mobile_number === "null" ? null : mobile_number,
    preferred_language,
    prescribed_medicine,
    support_type: support_type || ""
  });

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    const requiredFields: (keyof PatientDetails)[] = [
      'first_name', 
      'last_name', 
      'mobile_number', 
      'birth_date', 
      'preferred_language',
      'prescribed_medicine',
      'support_type'
    ];

    requiredFields.forEach(field => {
      if (!patientDetails[field]) {
        errors[field] = `${field.replace(/_/g, ' ')} is required`;
      }
    });

    if (patientDetails.mobile_number && !/^60\d{9,10}$/.test(patientDetails.mobile_number)) {
      errors.mobile_number = 'Invalid format (60XXXXXXXXX)';
    }

    setErrorInputMessage(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      setIsSaving(true);
      const res = await manuallyPatientUpdate({
        program_id,
        patient_id,       
        payload: {                      
            mobile_number: patientDetails.mobile_number || undefined,         
            first_name: patientDetails.first_name, 
            last_name: patientDetails.last_name, 
            metadata: {
            preferred_language: patientDetails.preferred_language,
            prescribed_medicine: patientDetails.prescribed_medicine,
            birth_date: patientDetails.birth_date,
            support_type: patientDetails.support_type
            }        
        }
      });

      if (res) {
        setOpenModal(false);
        setConfirmation(res.message);
      }
    } catch (error) {
      const err = error as ApiError;
      if (err?.inputs && err.message === 'ValidationError') {
        setErrorInputMessage(err.inputs);
      } else if (err?.message) {
        alert(err.message);
      }
    } finally {
      setLoading(false);
      setIsSaving(false);
    }
  }

  const onClick = () => {
    setPatientDetails({
      first_name,
      last_name,
      birth_date,
      mobile_number: mobile_number === "null" ? null : mobile_number,
      preferred_language,
      prescribed_medicine,
      support_type: support_type || ""
    });
    setErrorInputMessage({});
    setOpenModal(true);
  }

  const onFieldInput = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const name = e.target.name as keyof PatientDetails;
    const value = e.target.value as string;
    
    if (!name) return;
    
    if (errorInputMessage[name]) {
      setErrorInputMessage(prev => ({ ...prev, [name]: undefined }));
    }
    
    if (name === "prescribed_medicine") {
      setPatientDetails(prev => {
        if (prev.prescribed_medicine !== value) {
          return {
            ...prev, 
            [name]: value,
            support_type: value === "benlysta" ? "AFN" : ""
          };
        }
        return { ...prev, [name]: value };
      });
    } else {
      setPatientDetails(prev => ({ ...prev, [name]: value }));
    }
  }

  const handleBirthDateChange = (date: string | null) => {
    setPatientDetails(prev => ({ ...prev, birth_date: date }));
    if (errorInputMessage.birth_date) {
      setErrorInputMessage(prev => ({ ...prev, birth_date: undefined }));
    }
  }

  const handleClose = (reload: boolean) => {
    setOpenModal(false);
    setConfirmation(false);
    if (reload) window.location.reload();
  }

  const formContent = (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField 
          required
          label='First Name'
          name='first_name' 
          value={patientDetails.first_name}
          variant='outlined'
          className={classes.field}
          onChange={onFieldInput}
          error={!!errorInputMessage.first_name}
          InputLabelProps={{ className: classes.requiredLabel }}
        />
        <Typography variant='caption' display='block' gutterBottom color='error'>
          {errorInputMessage?.first_name || ''}
        </Typography>
      </Grid>
      
      <Grid item xs={6}>
        <TextField 
          required
          label='Last Name'
          name='last_name' 
          value={patientDetails.last_name}
          variant='outlined'
          className={classes.field}
          onChange={onFieldInput}
          error={!!errorInputMessage.last_name}
          InputLabelProps={{ className: classes.requiredLabel }}
        />
        <Typography variant='caption' display='block' gutterBottom color='error'>
          {errorInputMessage?.last_name || ''}
        </Typography>
      </Grid>
      
      {/* Email Field - Disabled but required */}
      <Grid item xs={6}>
        <TextField 
          disabled
          label='Email'
          name='email'
          value={email || ''}
          variant='outlined'
          className={classes.field}
          required
          InputLabelProps={{ className: classes.requiredLabel }}
        />
      </Grid>
      
      <Grid item xs={6}>
        <TextField  
          label='WhatsApp Number'
          required
          name='mobile_number' 
          value={patientDetails.mobile_number || ''}
          variant='outlined'
          className={classes.field}
          onChange={onFieldInput} 
          placeholder='60XXXXXXXXX'
          error={!!errorInputMessage.mobile_number}
          InputLabelProps={{ className: classes.requiredLabel }}
        />
        <Typography variant='caption' display='block' gutterBottom color='error'>
          {errorInputMessage?.mobile_number || ''}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <InputLabel required className={classes.requiredLabel}>Date of Birth</InputLabel>
        <DatePicker
          required
          onChange={handleBirthDateChange}
          name='birth_date'
          className={classes.field} 
          maxDate={currentDate}
          value={patientDetails.birth_date}
          error={!!errorInputMessage.birth_date}
        />
        <Typography variant='caption' display='block' gutterBottom color='error'>
          {errorInputMessage?.birth_date || ''}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <FormControl variant="outlined" className={classes.field} fullWidth>
          <InputLabel required className={classes.requiredLabel}>Preferred Language</InputLabel>
          <Select 
            required
            name="preferred_language"
            value={patientDetails.preferred_language}
            onChange={onFieldInput}
            error={!!errorInputMessage.preferred_language}
            label="Preferred Language"
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="bm">Bahasa Melayu</MenuItem>
            <MenuItem value="zh">Simplified Chinese</MenuItem>
          </Select>
        </FormControl>
        <Typography variant='caption' display='block' gutterBottom color='error'>
          {errorInputMessage?.preferred_language || ''}
        </Typography>
      </Grid>
      
      <Grid item xs={6}>
        <FormControl variant="outlined" className={classes.field} fullWidth>
          <InputLabel required className={classes.requiredLabel}>Prescribed Medicine</InputLabel>
          <Select 
            required
            name="prescribed_medicine"
            value={patientDetails.prescribed_medicine}
            onChange={onFieldInput}
            error={!!errorInputMessage.prescribed_medicine}
            label="Prescribed Medicine"
          >
            <MenuItem value="nucala">Nucala (mepolizumab)</MenuItem>
            <MenuItem value="benlysta">Benlysta (belimumab)</MenuItem>
          </Select>
        </FormControl>
        <Typography variant='caption' display='block' gutterBottom color='error'>
          {errorInputMessage?.prescribed_medicine || ''}
        </Typography>
      </Grid>
      
      {/* Support Type Field - Options based on medicine */}
      <Grid item xs={6}>
        <FormControl variant="outlined" className={classes.field} fullWidth>
          <InputLabel required className={classes.requiredLabel}>Support Type</InputLabel>
          <Select 
            required
            name="support_type"
            value={patientDetails.support_type}
            onChange={onFieldInput}
            error={!!errorInputMessage.support_type}
            label="Support Type"
          >
            {getSupportTypeOptions(patientDetails.prescribed_medicine)}
          </Select>
        </FormControl>
        <Typography variant='caption' display='block' gutterBottom color='error'>
          {errorInputMessage?.support_type || ''}
        </Typography>
      </Grid>
    </Grid>
  );

  const formButtons = (
    <>
      <Button onClick={() => handleClose(false)} variant='contained'>
        Close
      </Button>
      <Button  
        startIcon={isSaving ? <CircularProgress size={20} /> : null}
        disabled={isSaving}
        onClick={onSubmit} variant='contained' color='secondary'>
        Submit
      </Button>
    </>
  );

  return (
    <>
      <Dialog
        open={openModal}
        title='Update Patient'
        content={formContent} 
        children={formButtons}
        onSubmit = {onSubmit}
        onClose={() => handleClose(false)}
      />
      
      <Dialog 
        open={openConfirmation}
        title='Confirmation'
        content='Customer details successfully updated!' 
        children={(
          <Button onClick={() => handleClose(true)} variant='contained'>
            Close
          </Button>
        )}
        onSubmit = {onSubmit}
        onClose={() => handleClose(true)}
      />
      
      <Button variant="contained" onClick={onClick} style={{ marginRight: 8 }}>
        Update Details
      </Button>
      
      {isLoading && <LoadingOverlay />}
    </>
  )
}

export default UpdateCustomerDetails;